// Axios client with JWT + automatic refresh interceptor.

import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuth } from "../stores/auth";

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
  timeout: 30000,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = useAuth.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  // 메일 편지함 전환: 다른 사람/공용 편지함을 열람 중이면 /mail 요청에 mailbox 부착.
  // mailbox 파라미터를 선언하지 않은 엔드포인트(서명/규칙/계정 등)는 서버가 무시한다.
  if (activeMailbox != null && (config.url || "").startsWith("/mail")) {
    config.params = { ...(config.params || {}), mailbox: activeMailbox };
  }
  return config;
});

// 현재 열람 중인 편지함(백킹 사용자 id). null = 내 메일함. MailPage가 관리한다.
let activeMailbox: number | null = null;
export function setActiveMailbox(id: number | null): void { activeMailbox = id; }
export function getActiveMailbox(): number | null { return activeMailbox; }

let refreshInFlight: Promise<string> | null = null;

async function rotateRefresh(): Promise<string> {
  const refresh = useAuth.getState().refreshToken;
  if (!refresh) throw new Error("No refresh token");
  const res = await axios.post(
    `${api.defaults.baseURL}/auth/refresh`,
    { refresh_token: refresh },
    { timeout: 15000 },
  );
  const { access_token, refresh_token } = res.data;
  useAuth.getState().setTokens(access_token, refresh_token);
  return access_token;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    if (error.response?.status === 401 && !original._retried) {
      original._retried = true;
      try {
        if (!refreshInFlight) refreshInFlight = rotateRefresh().finally(() => { refreshInFlight = null; });
        const newToken = await refreshInFlight;
        original.headers?.set("Authorization", `Bearer ${newToken}`);
        return api(original);
      } catch (refreshError) {
        /*
         * 갱신이 실패했다고 무조건 로그아웃하면 안 된다.
         *
         * 예전에는 어떤 이유든 여기로 오면 세션을 지웠다. 그래서 신호가 잠깐 끊기거나
         * 서버가 재시작하는 몇 초 사이에 요청이 겹치면, 돌아왔을 때 로그인이 풀려
         * 있었다. "자꾸 풀린다" 던 것의 한 갈래다.
         *
         * 서버가 "이 토큰은 못 쓴다"(401·403)고 분명히 답했을 때만 지운다.
         * 네트워크 오류·타임아웃·5xx 는 그대로 두고 다음 기회에 다시 시도한다.
         */
        const status = (refreshError as AxiosError)?.response?.status;
        if (status === 401 || status === 403) useAuth.getState().logout();
        throw error;
      }
    }
    throw error;
  },
);
